<template>
  <el-row :gutter="16">
    <el-col :span="12">
      <PDatePicker
        v-model="date"
        type="date"
        :disabled="selecionarPeriodo"
        :error="!selecionarPeriodo && !!errors?.date"
        placeholder="Selecione uma data"
        label="Data"
        style="width: 100%"
        clearable
      />
    </el-col>
    <el-col :span="12" style="margin-top: 2px">
      <PSelect
        v-model="empresa"
        placeholder="Selecione uma empresa"
        label="Empresa"
        :options="empresasOpt"
        filterable
        clearable
      />
    </el-col>
    <el-col :span="24" style="margin: 0.7rem 0">
      <PCheckbox
        v-model="selecionarPeriodo"
        label="Selecionar um periodo"
        :disabled="periodoForcado"
      />
    </el-col>
    <el-col v-if="selecionarPeriodo" :span="12" style="margin-bottom: 16px">
      <PDatePicker
        v-model="inicio"
        type="date"
        :error="selecionarPeriodo && !inicio"
        placeholder="Selecione uma data de inicio"
        label="Inicio"
        style="width: 100%"
        clearable
      />
    </el-col>
    <el-col v-if="selecionarPeriodo" :span="12" style="margin-bottom: 16px">
      <PDatePicker
        v-model="fim"
        type="date"
        :error="selecionarPeriodo && !fim"
        placeholder="Selecione uma data de fim"
        label="Fim"
        style="width: 100%"
        clearable
      />
    </el-col>
  </el-row>
</template>

<script setup lang="ts">
import { PCheckbox, PDatePicker, PSelect } from "@f360-dev/f360-prisma";
import {
  CadastroDeIntegracaoDi,
  CadastroDeIntegracaoGateway
} from "../../../cadastro/services/CadastroDeIntegracaoGateway";
import { TWebservice } from "../../../../services/PainelDeIntegracaoesGateway";
import { TAPIRules } from "../../../../../../@types/IntegracoesAPIRules";
import { diContainer } from "@/inversify.config";
import { useForm } from "vee-validate";
import { computed, onMounted } from "vue";
import {
  formNexaasSchema,
  IFormularioNexaasSchema
} from "./formularioNexaasSchema";

const props = defineProps<{
  webservice?: TWebservice;
  empresasOpt: { label: string; value: string }[];
  apiSelecionada?: TAPIRules;
}>();

const painelIntegracoesGateway = diContainer.get<CadastroDeIntegracaoGateway>(
  CadastroDeIntegracaoDi
);

const periodoForcado = computed(() => props.apiSelecionada?.forcesRange === true);

const { handleSubmit, errors, setValues, values, defineField } =
  useForm<IFormularioNexaasSchema>({
    validationSchema: formNexaasSchema,
    initialValues: {
      date: new Date().toISOString().split("T")[0],
      empresa: "",
      selecionarPeriodo: periodoForcado.value,
      inicio: null,
      fim: null
    }
  });

const [date] = defineField("date");
const [empresa] = defineField("empresa");
const [selecionarPeriodo] = defineField("selecionarPeriodo");
const [inicio] = defineField("inicio");
const [fim] = defineField("fim");

const periodoUltrapassaLimiteDaApi = (): boolean => {
  const limite = props.apiSelecionada?.intervaloData;
  if (limite == null || limite <= 0) return false;

  const dataInicio = inicio.value != null ? new Date(inicio.value) : null;
  const dataFim = fim.value != null ? new Date(fim.value) : null;

  if (!dataInicio || !dataFim) return false;

  const diffEmDiasInicio = dataInicio.getDate();
  const diffEmDiasFim = dataFim.getDate();
  const diffEmDias = diffEmDiasFim - diffEmDiasInicio;

  if (diffEmDias < 0) return true;

  return diffEmDias >= limite;
};

const disabledSubmit = computed(() => {
  const missingDate = !date.value || String(date.value).trim() === "";
  const missingEmpresa = !empresa.value || String(empresa.value).trim() === "";

  if (missingDate || missingEmpresa) return true;

  if (selecionarPeriodo.value) {
    const missingInicio =
      inicio.value == null || String(inicio.value).trim() === "";
    const missingFim = fim.value == null || String(fim.value).trim() === "";

    return missingInicio || missingFim || periodoUltrapassaLimiteDaApi();
  }

  return false;
});

const obterEmpresaPorConfiguracao = async () => {
  if (!painelIntegracoesGateway) return;

  const response = await painelIntegracoesGateway.obterEmpresasParaNexaas();

  setValues({
    date: new Date().toISOString().split("T")[0],
    empresa: response?.Result?.[0]?.Value ?? "",
    selecionarPeriodo: periodoForcado.value,
    inicio: null,
    fim: null
  });
};

onMounted(async () => {
  try {
    await obterEmpresaPorConfiguracao();
  } catch (error) {
    console.error(error);
  }
});

defineExpose({
  handleSubmit,
  errors,
  values,
  disabledSubmit
});
</script>

<style lang="scss" scoped></style>
